'use client';
import { useState } from 'react';

export function PrivacyControls() {
    const [isExporting, setIsExporting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [message, setMessage] = useState<{
        type: 'success' | 'error';
        text: string;
    } | null>(null);
    const handleDataExport = async () => {
        setIsExporting(true);
        setMessage(null);
        try {
            const response = await fetch('/api/gdpr/export');
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `korvo-data-export-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                setMessage({ type: 'success', text: 'Data export completed successfully' });
            }
            else {
                const errorData = (await response.json()) as {
                    error?: string;
                };
                setMessage({ type: 'error', text: errorData.error || 'Failed to export data' });
            }
        }
        catch {
            setMessage({ type: 'error', text: 'An error occurred while exporting data' });
        }
        finally {
            setIsExporting(false);
        }
    };
    const handleDataDeletion = async () => {
        if (deleteConfirmation !== 'DELETE_MY_DATA') {
            setMessage({ type: 'error', text: 'Please type "DELETE_MY_DATA" to confirm' });
            return;
        }
        setIsDeleting(true);
        setMessage(null);
        try {
            const response = await fetch('/api/gdpr/delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    confirmation: deleteConfirmation
                }),
            });
            const data = (await response.json()) as {
                error?: string;
            };
            if (response.ok) {
                setMessage({ type: 'success', text: 'All your data has been permanently deleted. You will be redirected to the login page.' });
                // Redirect after a short delay
                setTimeout(() => {
                    window.location.href = '/auth/signin';
                }, 3000);
            }
            else {
                setMessage({ type: 'error', text: data.error || 'Failed to delete data' });
            }
        }
        catch {
            setMessage({ type: 'error', text: 'An error occurred while deleting data' });
        }
        finally {
            setIsDeleting(false);
        }
    };
    return (<div className="space-y-8">
      {/* Data Export Section */}
      <div className="border border-border rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="text-lg font-medium text-foreground-default mb-2">Export Your Data</h4>
            <p className="text-sm text-foreground-muted mb-4">
              Download a complete copy of all your data in JSON format. This includes your profile, 
              activity history, chat threads, and all associated data.
            </p>
            <button onClick={() => { void handleDataExport(); }} disabled={isExporting} className="inline-flex items-center px-4 py-2 border border-border shadow-sm text-sm font-medium rounded-md text-foreground-default bg-background-secondary hover:bg-background-default focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-default disabled:opacity-50 disabled:cursor-not-allowed">
              {isExporting ? (<>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-foreground-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </>) : (<>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  Export Data
                </>)}
            </button>
          </div>
        </div>
      </div>

      {/* Data Deletion Section */}
      <div className="border border-danger-default/30 rounded-lg p-6 bg-background-muted">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="text-lg font-medium text-danger-default mb-2">Delete All Your Data</h4>
            <p className="text-sm text-foreground-muted mb-4">
              <strong>Warning:</strong> This action cannot be undone. This will permanently delete all your data including:
            </p>
            <ul className="text-sm text-foreground-muted mb-4 list-disc list-inside">
              <li>Your profile and account information</li>
              <li>All chat threads and messages</li>
              <li>Activity history and audit logs</li>
              <li>All associated data and settings</li>
            </ul>
            <p className="text-sm text-foreground-muted mb-4">
              You will be logged out and redirected to the login page after deletion.
            </p>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="deleteConfirmation" className="block text-sm font-medium text-danger-default mb-2">
                  Type &quot;DELETE_MY_DATA&quot; to confirm:
                </label>
                <input id="deleteConfirmation" type="text" value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} className="block w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-danger-default focus:border-danger-default sm:text-sm bg-background-secondary text-foreground-default" placeholder="DELETE_MY_DATA"/>
              </div>
              
              <button onClick={() => { void handleDataDeletion(); }} disabled={isDeleting || deleteConfirmation !== 'DELETE_MY_DATA'} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-danger-foreground bg-danger-default hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-default disabled:opacity-50 disabled:cursor-not-allowed">
                {isDeleting ? (<>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>) : (<>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                    Permanently Delete All Data
                  </>)}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (<div className={`rounded-md p-4 ${message.type === 'success'
                ? 'bg-success-default/10 border border-success-default/30'
                : 'bg-danger-default/10 border border-danger-default/30'}`}>
          <p className={`text-sm ${message.type === 'success' ? 'text-success-default' : 'text-danger-default'}`}>
            {message.text}
          </p>
        </div>)}
    </div>);
}
